import { type ComponentProps, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  SEND_PHONE_VERIFICATION_CODE,
  VALIDATE_PHONE_VERIFICATION_CODE,
} from '@/src/common/gql/mutations/user'
import { useUserStore } from '@/src/common/store/useUserStore'
import { ScrollView, View, Keyboard, Text, StyleSheet } from 'react-native'
import { InputCode } from '@/src/common/components/Input/InputCode'
import { BackButton } from '@/src/common/components/BackButton'
import { OnboardingWrapper } from '@/src/common/wrappers/OnboardingWrapper'
import { useLogError } from '@/src/common/hooks/useLogError'
import { Button } from '@/src/common/components/Button'
import { RESEND_CODE_TIMER, VERIFICATION_CODE_LENGTH } from '@/src/common/utils/constants'
import { useRouter } from 'expo-router'
import { useGetUserInformation } from '@/src/common/hooks/useGetUserInformation'
import { triggerSegmentAction, triggerSegmentIdentify } from '@/src/services/segment'
import { formatPhoneNumberForBackend } from '@/src/common/utils/formatPhoneNumberForBackend'
import { AuthenticationE2E } from '@/src/experiences/authentication/e2e'
import { useSardine } from '@/src/services/Sardine'
import { sardineIdKey } from '@/src/services/Sardine/SardineId'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useEvent } from 'tamagui'

const ResendCodeButton = ({
  onResendCode,
  endTime,
}: {
  onResendCode: () => void
  endTime: Date
}) => {
  const [counter, setCounter] = useState(45)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      const timeDiff = endTime.getTime() - now.getTime()
      const secondsLeft = Math.max(0, Math.ceil(timeDiff / 1000))
      setCounter(secondsLeft)
    }, 1000)

    return () => clearInterval(interval) // Cleans interval when component unmounts
  }, [endTime])

  const resendCodeDisabled = counter !== 0
  const buttonTitle = 'Resend code' + (resendCodeDisabled ? ` in ${counter}s` : '')

  return (
    <Button
      segmentAction="User Resent OTP Code"
      disabled={resendCodeDisabled}
      title={buttonTitle}
      onPress={onResendCode}
    />
  )
}

export function VerifyPhoneOTP({
  backButtonHref,
  postVerifyPhoneNumberRouterPush = undefined,
  disclaimer = true,
}: {
  backButtonHref: ComponentProps<typeof BackButton>['href']
  postVerifyPhoneNumberRouterPush?: Parameters<ReturnType<typeof useRouter>['push']>[0]
  disclaimer?: boolean
}) {
  const router = useRouter()
  const logError = useLogError()
  const { refreshUserInformation } = useGetUserInformation()
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { phoneNumber, countryCode, parsedPhoneNumber } = useUserStore()
  const [errors, setErrors] = useState<string>()

  const [verifyCodeSent, { loading: loadingOTP, error: verifyOTPError }] = useMutation(
    VALIDATE_PHONE_VERIFICATION_CODE,
  )
  const [sendOTPToUser] = useMutation(SEND_PHONE_VERIFICATION_CODE)

  const { submitData, trackTextChange, trackFocusChange } = useSardine()

  const [phoneVerificationCode, setPhoneVerificationCode] = useState('')
  const [endTime, setEndTime] = useState(new Date(Date.now() + RESEND_CODE_TIMER))

  function getPhoneNumberFormattedForBackend() {
    return phoneNumber === undefined ? '' : formatPhoneNumberForBackend(phoneNumber, countryCode)
  }

  const resendCode = () => {
    setPhoneVerificationCode('')
    setEndTime(new Date(Date.now() + RESEND_CODE_TIMER))
    void sendOTPToUser({ variables: { phoneNumber: getPhoneNumberFormattedForBackend() } })
  }

  const [verifying, setVerifying] = useState(false)

  async function verifyPhoneNumber() {
    try {
      setVerifying(true)
      await verifyCodeSent({
        variables: {
          phoneNumber: getPhoneNumberFormattedForBackend(),
          code: phoneVerificationCode,
        },
        onCompleted(response) {
          void (async function postVerifyPhoneNumber() {
            try {
              if (!response.validatePhoneVerificationCode.success) {
                setErrors(
                  `There appears to be an error with the either the phone number or verification code.`,
                )
                return
              } else if (verifyOTPError != null) {
                setErrors(verifyOTPError.message)
                return
              }

              /**
               * after the backend verifies the phone otp, the backend updates the user in the `users` table with that phone number.
               * in order to have this update sync'd to the frontend, we're calling `refreshUserInformation()` here.
               * `theNewUserInformationComingFromTheBackend` is the new `userInformation` coming in from the backend
               */
              const theNewUserInformationComingFromTheBackend = await refreshUserInformation() // Update user information to set new phone number
              if (theNewUserInformationComingFromTheBackend !== undefined) {
                void triggerSegmentIdentify({
                  userId: theNewUserInformationComingFromTheBackend.id,
                  userInformation: theNewUserInformationComingFromTheBackend,
                })

                void triggerSegmentAction({ action: 'Successful user log in', component: 'Button' })
              }
              if (postVerifyPhoneNumberRouterPush) {
                router.replace(postVerifyPhoneNumberRouterPush)
              }
            } finally {
              setVerifying(false)
            }
          })()
        },
      })
    } catch (error) {
      logError(error)
      setVerifying(false)
    }
  }

  const handleContinue = useEvent(() => {
    void verifyPhoneNumber()
    void submitData()
  })

  useEffect(() => {
    if (phoneVerificationCode.length === VERIFICATION_CODE_LENGTH) {
      handleContinue()
    }
  }, [phoneVerificationCode, handleContinue])

  return (
    <OnboardingWrapper paddingHorizontal={15} testID={AuthenticationE2E.Ids.View.VerifyOTP}>
      <ScrollView>
        <BackButton href={backButtonHref} text="Change your Number" />
        <View style={styles.headerText}>
          <Text style={typography.XXL}>Check Those Texts</Text>
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.instructionText, typography.Base]}>
            We just sent a code to {`${countryCode} ${parsedPhoneNumber}` + '\n'}
            Didn't receive the code? Resend below.
          </Text>
        </View>
        <View style={styles.inputCodeContainer}>
          <InputCode
            numberOfDigits={VERIFICATION_CODE_LENGTH}
            segmentAction="User Inputs OTP"
            onTextChange={(text) => {
              void trackTextChange('verificationCode', text)
              setPhoneVerificationCode(text)
            }}
            onFocus={() => void trackFocusChange('verificationCode', true)}
            onBlur={() => void trackFocusChange('verificationCode', false)}
            textInputProps={{ dataSet: { [sardineIdKey]: 'verificationCode' } }}
          />
        </View>
        <View style={styles.errorContainer}>
          <Text style={{ color: 'red' }}>{errors}</Text>
        </View>
        {disclaimer && (
          <View>
            <Text style={styles.disclaimerText}>
              IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT: To help the
              government fight the funding of terrorism and money laundering activities, Federal law
              requires all financial institutions to obtain, verify and record information that
              identifies each person who opens an account. What this means for you: When you open an
              account, we will ask for your name, address, date of birth and other information that
              will allow us to identify you. We may also ask to see your driver's license or other
              identifying documents.
            </Text>
          </View>
        )}
      </ScrollView>
      <View style={styles.buttonContainer}>
        <Button
          disabled={
            phoneVerificationCode.length !== VERIFICATION_CODE_LENGTH || loadingOTP || verifying
          }
          title="Continue"
          loading={verifying}
          onPress={() => {
            Keyboard.dismiss()
            handleContinue()
          }}
        />
      </View>
      <View style={styles.resendButtonContainer}>
        <ResendCodeButton onResendCode={resendCode} endTime={endTime} />
      </View>
    </OnboardingWrapper>
  )
}

const styles = StyleSheet.create({
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    lineHeight: 24,
  },
  disclaimerText: {
    marginTop: 16,
    lineHeight: 24,
    fontSize: 14,
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  inputCodeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    marginBottom: 10,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'red',
  },
  buttonContainer: {
    marginTop: 24,
    marginBottom: 16,
  },
  resendButtonContainer: {
    marginBottom: 32,
  },
})
